import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useUnsavedChangesStore } from '@/stores/unsavedChanges';
import { useAppDialog } from '@/composables/useAppDialog';

/** Vue Router may expose duplicate keys as string[]. */
export function firstQueryValue(value: string | string[] | undefined): string | undefined {
  if (Array.isArray(value)) return value[0];
  return value;
}

/**
 * Same-origin path for router navigation or ?redirect= (avoids open redirects and bad router.push inputs).
 */
export function safeInternalRedirectPath(raw: unknown): string {
  if (raw == null || typeof raw !== 'string') return '/';
  const s = raw;
  if (!s || s.length > 2048) return '/';
  let decoded = s;
  try {
    decoded = decodeURIComponent(s.trim());
  } catch {
    return '/';
  }
  if (decoded.startsWith('//') || /^\w+:/.test(decoded)) return '/';
  if (!decoded.startsWith('/')) return '/';
  const pathOnly = decoded.split(/[?#]/)[0] ?? '';
  if (!pathOnly.startsWith('/')) return '/';
  if (pathOnly === '/login' || pathOnly === '/login/') return '/';
  return decoded;
}

/**
 * When the guard runs for a URL that is already /login (e.g. unmatched /login/),
 * use the inner redirect target instead of wrapping fullPath (prevents nested ?redirect= on reload).
 */
function unwrapChainedLoginRedirects(redirect: string): string {
  let current = redirect;
  for (let depth = 0; depth < 8; depth++) {
    if (!current.startsWith('/login')) return current;
    const q = current.indexOf('?');
    if (q === -1) return '/';
    const inner = new URLSearchParams(current.slice(q + 1)).get('redirect');
    if (!inner) return '/';
    try {
      current = decodeURIComponent(inner);
    } catch {
      return '/';
    }
  }
  return '/';
}

function postAuthRedirectParam(to: RouteLocationNormalized): string {
  const onLoginPath = to.path === '/login' || to.path === '/login/';
  if (to.name === 'Login' || onLoginPath) {
    const raw = firstQueryValue(to.query.redirect as string | string[] | undefined);
    const inner = typeof raw === 'string' && raw.length > 0 ? raw : '/';
    return unwrapChainedLoginRedirects(inner);
  }
  return to.fullPath;
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    alias: '/login/',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  // Static hosts often use /index.html; treat it like app root for post-login redirects.
  { path: '/index.html', redirect: '/' },
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/flows',
    name: 'Flows',
    component: () => import('@/views/flows/FlowListView.vue'),
    meta: { requiresAuth: true, permission: 'flow:read' },
  },
  {
    path: '/flows/new',
    name: 'NewFlow',
    component: () => import('@/views/flows/FlowEditorView.vue'),
    meta: { requiresAuth: true, permission: 'flow:create' },
  },
  {
    path: '/flows/:id',
    name: 'EditFlow',
    component: () => import('@/views/flows/FlowEditorView.vue'),
    meta: { requiresAuth: true, permission: 'flow:update' },
  },
  {
    path: '/flows/:id/executions',
    name: 'FlowExecutions',
    component: () => import('@/views/flows/FlowExecutionsView.vue'),
    meta: { requiresAuth: true, permission: 'flow:read' },
  },
  {
    path: '/connections',
    name: 'Connections',
    component: () => import('@/views/connections/ConnectionListView.vue'),
    meta: { requiresAuth: true, permission: 'connection:read' },
  },
  {
    path: '/components',
    name: 'Components',
    component: () => import('@/views/components/ComponentListView.vue'),
    meta: { requiresAuth: true, permission: 'component:read' },
  },
  {
    path: '/components/:id',
    name: 'ComponentEdit',
    component: () => import('@/views/components/ComponentEditView.vue'),
    meta: { requiresAuth: true, permission: 'component:update' },
  },
  {
    path: '/executions',
    name: 'Executions',
    component: () => import('@/views/executions/ExecutionListView.vue'),
    meta: { requiresAuth: true, permission: 'execution:read' },
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/views/AdminView.vue'),
    meta: { requiresAuth: true, permission: 'user:create' },
  },
  {
    path: '/notifications',
    name: 'Notifications',
    component: () => import('@/views/notifications/NotificationsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/views/SettingsView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/guide',
    name: 'Guide',
    component: () => import('@/views/UserGuideView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/test',
    name: 'Test',
    component: () => import('@/views/TestView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/playwright-recorder',
    name: 'PlaywrightRecorder',
    component: () => import('@/views/flows/PlaywrightRecorderView.vue'),
    meta: { requiresAuth: false },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();
  const requiresAuth = to.meta.requiresAuth !== false;
  const permission = to.meta.permission as string | undefined;

  const onLogin =
    to.name === 'Login' || to.path === '/login' || to.path === '/login/';
  if (onLogin && authStore.isAuthenticated) {
    const dest = safeInternalRedirectPath(firstQueryValue(to.query.redirect as string | string[] | undefined));
    next(dest);
    return;
  }

  if (requiresAuth && !authStore.isAuthenticated) {
    const redirect = safeInternalRedirectPath(postAuthRedirectParam(to));
    next({ name: 'Login', query: { redirect } });
    return;
  }
  if (permission && !authStore.hasPermission(permission)) {
    next({ name: 'Dashboard' });
    return;
  }

  // Alert when navigating away with unsaved changes (in-app navigation)
  const unsavedStore = useUnsavedChangesStore();
  if (from.name && unsavedStore.hasUnsaved) {
    const { showConfirm } = useAppDialog();
    const leave = await showConfirm(
      'You have unsaved changes. Leave this page anyway?',
      { title: 'Unsaved changes', confirmLabel: 'Leave', cancelLabel: 'Stay', variant: 'warning' }
    );
    if (!leave) {
      next(false);
      return;
    }
    unsavedStore.clearUnsaved();
  }

  next();
});

export default router;
